import type { StepForms } from '@/stores/slices/setup-fund-slice';

import { isManagedAccount } from './utils/is-managed-account';

export type SubStepOrderEntry = {
  code: string;
  step: number;
  diffPrefixes: string[];
  /** Sub-steps a given fund never fills in are skipped by the wizard. */
  isApplicable?: (_forms: StepForms) => boolean;
};

export const SUB_STEP_ORDER: SubStepOrderEntry[] = [
  { code: '01', step: 1, diffPrefixes: ['investmentDescription'] },
  { code: '02', step: 1, diffPrefixes: ['participants'] },
  { code: '03', step: 1, diffPrefixes: ['duration'] },
  { code: '04', step: 1, diffPrefixes: ['returnProfile'] },
  { code: '05', step: 1, diffPrefixes: ['redemption'] },

  { code: '06', step: 2, diffPrefixes: ['feesData'] },
  { code: '07', step: 2, diffPrefixes: ['minInvestmentAmounts'] },
  { code: '08', step: 2, diffPrefixes: ['distributionPeriod'] },
  { code: '09', step: 2, diffPrefixes: ['reportingPeriod'] },

  { code: '10', step: 3, diffPrefixes: ['investorEligibility'] },
  { code: '11', step: 3, diffPrefixes: ['countrySpecificExemptions'] },

  {
    code: '12',
    step: 4,
    diffPrefixes: [
      'offeringDocuments',
      'subscriptionDocuments',
      'riskDisclosure',
      'previousYearsAudits',
    ],
  },

  { code: '13', step: 5, diffPrefixes: ['marketingMaterial'] },
  { code: '14', step: 5, diffPrefixes: ['cardImageId', 'coverImageId', 'logoId'] },
  { code: '15', step: 5, diffPrefixes: ['howFundWorks'] },
  { code: '16', step: 5, diffPrefixes: ['fundPresentation'] },
  { code: '17', step: 5, diffPrefixes: ['fundBenefits'] },
  { code: '18', step: 5, diffPrefixes: ['fundTargetInvestor'] },
  { code: '19', step: 5, diffPrefixes: ['managerAlignment'] },
  { code: '20', step: 5, diffPrefixes: ['faq'] },

  { code: '21', step: 6, diffPrefixes: ['investmentAvailabilityStatus', 'transferability'] },
  {
    code: '22',
    step: 6,
    diffPrefixes: ['numberUnits'],
    isApplicable: (forms) => !isManagedAccount(forms),
  },
  { code: '23', step: 6, diffPrefixes: ['subscriptionOptions'] },
];

export const TOP_LEVEL_STEPS = [1, 2, 3, 4, 5, 6];

export const subStepCount = SUB_STEP_ORDER.length;

export const codesForStep = (step: number) =>
  SUB_STEP_ORDER.filter((s) => s.step === step).map((s) => s.code);

export const subStepIndexByCode = (code: string) =>
  SUB_STEP_ORDER.findIndex((s) => s.code === code);

export const firstSubStepIndexOfStep = (step: number) =>
  SUB_STEP_ORDER.findIndex((s) => s.step === step);

export const lastSubStepIndexOfStep = (step: number) =>
  SUB_STEP_ORDER.reduce((last, s, i) => (s.step === step ? i : last), -1);

export const stepForSubStepIndex = (index: number) => SUB_STEP_ORDER[index]?.step;

export const stepForSubStepCode = (code: string) =>
  SUB_STEP_ORDER.find((s) => s.code === code)?.step;

export const isSubStepApplicable = (code: string, forms: StepForms) => {
  const entry = SUB_STEP_ORDER.find((s) => s.code === code);
  return entry ? (entry.isApplicable?.(forms) ?? true) : false;
};

export const nextApplicableSubStepIndex = (index: number, forms: StepForms) => {
  for (let next = index + 1; next < SUB_STEP_ORDER.length; next += 1) {
    if (isSubStepApplicable(SUB_STEP_ORDER[next].code, forms)) return next;
  }
  return null;
};

export const previousApplicableSubStepIndex = (index: number, forms: StepForms) => {
  for (let previous = index - 1; previous >= 0; previous -= 1) {
    if (isSubStepApplicable(SUB_STEP_ORDER[previous].code, forms)) return previous;
  }
  return null;
};
